<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ElUpload } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import type { UploadProps, UploadUserFile } from 'element-plus'

import { fileRulesTip, isAcceptedFile } from '@/shared/lib'

/**
 * One document picked for a form field — a PDF invoice, say. `ImageUpload` and not this for
 * a picture: that one previews it and checks the MIME type, which a PDF from some systems
 * does not carry reliably, so this one checks the extension instead. Not the legacy
 * `@/components/FileUpload.vue` either, which takes several files and previews media.
 *
 * Nothing is uploaded: the `File` goes into `v-model`, and the form sends it with the rest.
 * A file that fails the check is refused with a toast and the field is left empty. Without
 * `tip` the drop zone names the extensions and the size limit itself.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: File | null
    /** Extensions with the dot, comma-separated: `.pdf` or `.pdf,.doc`. */
    accept: string
    maxSizeMb: number
    tip?: string
    block?: boolean
  }>(),
  { modelValue: null, tip: '', block: false }
)

const emit = defineEmits<{
  (e: 'update:modelValue', file: File | null): void
}>()

const fileList = ref<UploadUserFile[]>([])

const shownTip = computed(
  () =>
    props.tip ||
    fileRulesTip({ accept: props.accept, maxSizeMb: props.maxSizeMb })
)

watch(
  () => props.modelValue,
  (file) => {
    fileList.value = file ? [{ name: file.name }] : []
  },
  { immediate: true }
)

const handleChange: UploadProps['onChange'] = (uploadFile) => {
  const raw = uploadFile.raw

  if (
    !raw ||
    !isAcceptedFile(raw, { accept: props.accept, maxSizeMb: props.maxSizeMb })
  ) {
    fileList.value = []
    emit('update:modelValue', null)
    return
  }

  emit('update:modelValue', raw)
}
</script>

<template>
  <ElUpload
    v-model:file-list="fileList"
    class="shared-file-upload"
    :class="{ 'shared-file-upload--block': block }"
    drag
    :accept="accept"
    :auto-upload="false"
    :limit="1"
    :on-exceed="() => {}"
    :on-change="handleChange"
    :on-remove="() => emit('update:modelValue', null)"
    action="#"
  >
    <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
    <div class="el-upload__text">
      Drop file here or <em>click to upload</em>
    </div>
    <template #tip>
      <div class="el-upload__tip">{{ shownTip }}</div>
    </template>
  </ElUpload>
</template>

<style lang="scss" scoped>
.shared-file-upload--block {
  width: 100%;
}
</style>
